/**
 * Typed error thrown by {@link apiFetch} on any non-OK HTTP response.
 *
 * Callers that need to distinguish 409 / 429 / 404 / 500 or branch on a
 * structured `error.code` (e.g. `RECLASSIFY_IN_FLIGHT`, `RATE_LIMITED`)
 * should `instanceof ApiError` and read `status` / `code` directly
 * rather than regex-matching `err.message` — the message is copy, not
 * contract. `ApiError extends Error`, so existing callers that merely
 * stringify `err.message` continue to work unchanged.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Authenticated fetch wrapper for API calls.
 *
 * Accepts a Clerk session token and prefixes paths with /api if needed.
 * All dashboard hooks pass the token obtained from useAuth().getToken().
 *
 * On non-OK responses, throws an {@link ApiError} carrying the HTTP
 * status and the structured `error.code` from the route-thrown body
 * shape (or `null` when the body uses the Zod-validator shape, is
 * non-JSON, or is absent).
 */
export async function apiFetch<T>(
  path: string,
  token: string | null,
  options?: RequestInit,
): Promise<T> {
  const url = path.startsWith('/api') ? path : `/api${path}`;

  const headers: HeadersInit = {
    ...(options?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...options?.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    // Our routes use two error shapes:
    //   { error: { code, message } }           — route-thrown errors
    //   { statusCode, error, message }         — shared Zod validator (task 050)
    // Fall back to statusText if neither shape is present.
    const message =
      (body as { error?: { message?: string } })?.error?.message ??
      (body as { message?: string })?.message ??
      `API error: ${response.status} ${response.statusText}`;
    // `code` is only present on the route-thrown shape. The Zod-validator
    // shape has no code field, and a non-JSON / absent body yields null.
    const code = (body as { error?: { code?: string } })?.error?.code ?? null;
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) return undefined as T;

  return response.json() as Promise<T>;
}
